import '../Profile.css'
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid2, InputAdornment, OutlinedInput } from "@mui/material";
import ButtonMedium1 from "components/elements/ButtonMedium1";
import ButtonMedium4 from "components/elements/ButtonMedium4";
import ContentNormal from "components/elements/ContentNormal";
import ContentExtraSmall from "components/elements/ContentExtraSmall";
import CustomCheckbox from "components/elements/CustomCheckbox";
import Headline4 from "components/elements/Headline4";
import Headline5 from "components/elements/Headline5";
import { AppContext } from "contexts/AppContext";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { callAPI } from "utils/API";
import { isValidPhoneNumber } from "utils/general";
import { setupUserData } from "utils/setup";
import { translate } from "utils/Translate";

const SettingsPage = () => {
    const context = useContext(AppContext);
    const { userData, styleGuide, brandData } = context;

    const getCountryCodeOfPhoneNumber = (phoneNumber = "") => {
        if (!phoneNumber || !phoneNumber.includes("+")) {
            return ""
        }
        const allowedCountryCodes = ["+29", "+35", "+44", "+45", "+46", "+47", "+49"]
        for (const code of allowedCountryCodes) {
            if (phoneNumber.includes(code)) return code
        }
        return ""
    }

    const enableMobileNumberUpdates = brandData?.features?.enableMobileNumberUpdates || false
    const countryCode = enableMobileNumberUpdates ? brandData?.features?.mobileNumberCountryCode || "+45" : getCountryCodeOfPhoneNumber(userData?.userPhone) || ""
    const contactEmail = brandData?.contactEmail || `support+${brandData?.brandSlug}@upgrader.club`
    const mobileNumberLength = 8
    const userPhone = userData?.userPhone?.split(countryCode)
    const [userMobileNumber, setUserMobileNumber] = useState(userPhone[1] || "")
    const [loading, setLoading] = useState(false)
    const [showProfileUpdateSuccessModal, setShowProfileUpdateSuccessModal] = useState(false)
    const [showProfileUpdateErrorModal, setShowProfileUpdateErrorModal] = useState(false)
    const [showMobileNumberError, setShowMobileNumberError] = useState(false)
    const [userProfile, setUserProfile] = useState(userData)
    const [smsConsentLoading, setSmsConsentLoading] = useState(false)
    const [emailConsentLoading, setEmailConsentLoading] = useState(false)

    const hideNotificationPreference = brandData?.features?.hideNotificationPreferenceEditProfile || false
    const enableNameUpdates = brandData?.features?.enableNameUpdates
    const enableEmailUpdates = brandData?.features?.enableEmailUpdates
    const enableCompanyUpdates = brandData?.features?.enableCompanyUpdates
    const enableAddressUpdates = brandData?.features?.enableAddressUpdates
    const enableProfileEdit = enableNameUpdates || enableEmailUpdates || enableCompanyUpdates || enableAddressUpdates

    const handleMobileNumberChange = (mobileNumberInput = "") => {
        setUserMobileNumber(mobileNumberInput)
    }

    const updateUserProfile = async () => {
        return await callAPI("/user/updateUserProfile", "POST", { body: userProfile })
    }

    const saveChanges = async () => {
        setLoading(true)

        let mobileNumberInput = userMobileNumber
        if (!userMobileNumber.startsWith(countryCode)) {
            mobileNumberInput = `${countryCode}${mobileNumberInput}`
        }
        userProfile.userPhone = mobileNumberInput
        
        const response = await updateUserProfile()
        if (response === true) {
            setupUserData(context, setLoading)
            setShowProfileUpdateSuccessModal(true)
        } else {
            setLoading(false)
            setShowProfileUpdateErrorModal(true)
        }
    }

    const updateCommercialSMSConsent = async () => {
        setSmsConsentLoading(true)
        await callAPI("/user/updateCommercialSMSConsent", "POST")
        setupUserData(context, setSmsConsentLoading)
    }

    const updateCommercialEmailConsent = async () => {
        setEmailConsentLoading(true)
        await callAPI("/user/updateCommercialEmailConsent", "POST")
        setupUserData(context, setEmailConsentLoading)
    }

    useEffect(() => {
        if (enableMobileNumberUpdates) {
            const isValid = isValidPhoneNumber(countryCode, mobileNumberLength, userMobileNumber)
            setShowMobileNumberError(!isValid)
        }
    }, [userMobileNumber])

    return (
        <Box flex={1} display={'flex'} gap={4} flexDirection={'column'}>
            <Accordion
                className="profileAccordion"
                defaultExpanded={true}
            >
                <AccordionSummary className="profileAccordionSummary">
                    <Box display={'flex'} flex={1} flexDirection={'column'} alignContent={'start'} gap={2}>
                        <Box display={'flex'} gap={2} justifyContent={'space-between'} alignItems={'center'}>
                            <Headline4
                                translationKey="_PROFILE_MIN_PROFILE_"
                                fallbackText="Din profil"
                            />
                            {!enableProfileEdit &&
                                <ButtonMedium4
                                    style={{minWidth: 120}}
                                    showIcon={false}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        window.location.href = `mailto:${contactEmail}`;
                                    }}
                                    translationKey="_PROFILE_SEND_MESSAGE_BUTTON_"
                                    fallbackText="Skriv besked"
                                />
                            }
                        </Box>
                        {!enableProfileEdit &&
                            <Box>
                                <ContentNormal
                                    translationKey="_PROFILE_WRONG_INFORMATION_INFO_"
                                    fallbackText={`Hvis der er fejl i dine oplysninger, bedes du kontakte `}
                                />
                                <ContentNormal
                                    fallbackText={contactEmail}
                                />
                            </Box>
                        }
                    </Box>
                </AccordionSummary>
                <AccordionDetails
                    style={{
                        borderTop: `1px solid ${styleGuide.color4}`,
                        overflow: 'scroll'
                    }}
                    className="hide-scroll profileAccordionDetails"
                >
                    <Box display={'flex'} flexWrap={'wrap'} columnGap={20} rowGap={8}>
                        <Box display={'flex'} flex={1} flexDirection={'column'} gap={4}>
                            <Headline5
                                translationKey="_PROFILE_YOUR_INFORMATION_HEADLINE_"
                                fallbackText="Dine oplysninger"
                            />

                            <ProfileSettingsInput
                                type='text'
                                label={translate("_PROFILE_EDIT_FIELD_LABEL_NAME_", "Fulde navn")}
                                value={userProfile.userName}
                                onChange={(e) => setUserProfile({...userProfile, userName: e.target.value})}
                                readOnly={!enableNameUpdates}
                            />

                            <ProfileSettingsInput
                                type='date'
                                label={translate("_BIRTHDATE_", "Fødselsdato")}
                                value={userProfile.userBirthday}
                                onChange={(e) => setUserProfile({...userProfile, userBirthday: e.target.value})}
                            />

                            <ProfileSettingsInput
                                type='text'
                                label={translate("_PROFILE_EDIT_FIELD_LABEL_EMAIL_", "E-mail")}
                                value={userProfile.userEmail}
                                onChange={(e) => setUserProfile({...userProfile, userEmail: e.target.value})}
                                readOnly={!enableEmailUpdates}
                            />

                            <ProfileSettingsInput
                                type='text'
                                label={translate("_PROFILE_EDIT_FIELD_LABEL_TELEPHONE_", "Telefon")}
                                value={userMobileNumber}
                                onChange={(e) => handleMobileNumberChange(e.target.value)}
                                readOnly={!enableMobileNumberUpdates}
                                startAdornment={<InputAdornment position="start">{countryCode}</InputAdornment>}
                                error={showMobileNumberError}
                                prefix={countryCode}
                                errorText={
                                    translate(
                                        "_PROFILE_INVALID_TELEPHONE_TEXT_" , 
                                        "Mobile number should be 8 digits which is automatically prefixed by Country code"
                                    )
                                }
                            />
                        </Box>

                        <Box display={'flex'} flex={1} flexDirection={'column'} gap={4}>
                            <Headline5
                                translationKey="_PROFILE_COMPANY_INFORMATION_"
                                fallbackText="Firmaoplysninger"
                            />

                            <ProfileSettingsInput
                                type='text'
                                label={translate("_PROFILE_EDIT_FIELD_LABEL_COMPANY_", "Firmanavn")}
                                value={userProfile.userCompany}
                                onChange={(e) => setUserProfile({...userProfile, userCompany: e.target.value})}
                                readOnly={!enableCompanyUpdates}
                            />

                            <ProfileSettingsInput
                                type='text'
                                label={translate("_PROFILE_EDIT_FIELD_LABEL_COMPANY_ADDRESS_", "Firmaadresse")}
                                value={userProfile.userAddress}
                                onChange={(e) => setUserProfile({...userProfile, userAddress: e.target.value})}
                                readOnly={!enableAddressUpdates}
                            />

                            <Box display={'flex'} gap={4}>
                                <Box flex={1}>
                                    <ProfileSettingsInput
                                        type='postalcode'
                                        label={translate("_PROFILE_EDIT_FIELD_LABEL_ZIPCODE_", "Postnummer")}
                                        value={userProfile.userZipcode}
                                        onChange={(e) => setUserProfile({...userProfile, userZipcode: e.target.value})}
                                        readOnly={!enableAddressUpdates}
                                    />

                                </Box>

                                <Box flex={3}>
                                    <ProfileSettingsInput
                                        type='text'
                                        label={translate("_PROFILE_EDIT_FIELD_LABEL_CITY_", "By")}
                                        value={userProfile.userCity}
                                        onChange={(e) => setUserProfile({...userProfile, userCity: e.target.value})}
                                        readOnly={!enableAddressUpdates}
                                    />
                                </Box>
                            </Box>

                            <Box>
                                <ButtonMedium1
                                    showIcon={false}
                                    translationKey="_BUTTON_CAPTION_SAVE_"
                                    fallbackText="Gem ændringer"
                                    onClick={() => saveChanges()}
                                    disabled={!!loading || showMobileNumberError}
                                />
                            </Box>
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* Notification Consent settings */}
            <Accordion
                className="profileAccordion"
                defaultExpanded={true}
            >
                <AccordionSummary className="profileAccordionSummary">
                    <Box flexDirection={'column'} display={'flex'} gap={2}>
                        <Headline4
                            translationKey="_PROFILE_SETTINGS_PREFERENCES_HEADLINE_"
                            fallbackText="Præferencer"
                        />
                    </Box>
                    
                </AccordionSummary>

                <AccordionDetails
                    style={{
                        borderTop: `1px solid ${styleGuide.color4}`,
                        overflow: 'scroll'
                    }}
                    className="hide-scroll profileAccordionDetails"
                >
                    <Box display={'flex'} flexDirection={'column'} gap={4}>
                        {!!brandData?.features?.showCategoryPreference &&
                            <ProfileSettingCategoryPreference />
                        }

                        <Box display={'flex'} flexDirection={'column'} gap={2}>
                            <Headline5 translationKey="_PROFILE_ADJUST_NOTIFICATION_SETTINGS_" fallbackText="Tilpas dine notifikationer" />
                            <ContentNormal translationKey="_PROFILE_NOTIFICATION_CONSENT_NECESSARY_TEXT_" fallbackText="Nødvendige beskeder så som adgangskoder, ordre, point udløb mv." />
                            <Box display={'flex'} gap={2}>
                                <CustomCheckbox
                                    disabled
                                    checked={true}
                                    label={translate("_SMS_", "SMS")}
                                />
                                <CustomCheckbox
                                    disabled
                                    checked={true}
                                    label={translate("_EMAIL_", "Email")}
                                />
                            </Box>
                            {
                                !hideNotificationPreference &&
                                <>
                                    <ContentNormal translationKey="_PROFILE_NOTIFICATION_CONSENT_COMMERCIAL_TEXT_" fallbackText="Kommercielle beskeder som nyheder, ”lagersalg”, nye produkter mv." />
                                    <Box display={'flex'} gap={2}>
                                        <CustomCheckbox
                                            checked={!!userData?.consentNews?.SMS}
                                            label={translate("_SMS_", "SMS")}
                                            onChange={() => {
                                                updateCommercialSMSConsent()
                                            }}
                                            loading={smsConsentLoading}
                                        />
                                        <CustomCheckbox
                                            checked={!!userData?.consentNews?.EMAIL}
                                            label={translate("_EMAIL_", "Email")}
                                            onChange={() => {
                                                updateCommercialEmailConsent()
                                            }}
                                            loading={emailConsentLoading}
                                        />
                                    </Box>
                                </>
                            }

                        </Box>

                    </Box>
                </AccordionDetails>
            </Accordion>
            
            <Modal show={showProfileUpdateSuccessModal} onHide={() => setShowProfileUpdateSuccessModal(false)} style={{ margin: 'auto' }} centered>
                <Modal.Header className="pb-0 border-0" closeButton style={{ fontSize: 9 }}>
                    <Headline5 translationKey="_PROFILE_UPDATE_">Opdateret</Headline5>
                </Modal.Header>
                <Modal.Body>
                    <ContentNormal translationKey="_PROFILE_UPDATE_TEXT_">Profil er opdateret</ContentNormal>
                </Modal.Body>
                <Modal.Footer>
                    <Box display={'flex'} float='right'>
                        <ButtonMedium1 showIcon={false} translationKey="_OK_" fallbackText="OK" onClick={() => setShowProfileUpdateSuccessModal(false)} />
                    </Box>
                </Modal.Footer>
            </Modal>

            <Modal show={showProfileUpdateErrorModal} onHide={() => setShowProfileUpdateErrorModal(false)} style={{ margin: 'auto' }} centered>
                <Modal.Header className="pb-0 border-0" closeButton style={{ fontSize: 9 }}>
                    <Headline5 translationKey="_PROFILE_UPDATE_ERROR_">Der opstod en fejl</Headline5>
                </Modal.Header>
                <Modal.Body>
                    <ContentNormal translationKey="_PROFILE_UPDATE_ERROR_TEXT_">Din profil kunne ikke opdateres</ContentNormal>
                </Modal.Body>
                <Modal.Footer>
                    <Box display={'flex'} float='right'>
                        <ButtonMedium1 showIcon={false} translationKey="_OK_" fallbackText="OK" onClick={() => setShowProfileUpdateErrorModal(false)} />
                    </Box>
                </Modal.Footer>
            </Modal>
        </Box>
    )
}

const ProfileSettingsInput = (props) => {
    const context = useContext(AppContext);
    const { styleGuide } = context;

    const inputStyle = {
        borderRadius: 14,
        border: `1px solid ${props.error ? styleGuide.color12 : '#777'}`,
        height: 45,
    }

    const value = props.type === 'date' ? moment(props.value).format('YYYY-MM-DD') : props.value

    return (
        <Box display={'flex'} flexDirection={'column'} gap={1}>
            <ContentNormal>
                {props.label}
            </ContentNormal>

            {props.readOnly
            ?
                <ContentNormal style={{color: styleGuide.color5}}>
                    {value || ' '}
                </ContentNormal>
            :
                <OutlinedInput
                    type={props.type}
                    style={inputStyle}
                    value={value}
                    onChange={props.onChange}
                    disabled={props.disabled}
                    startAdornment={props.startAdornment}
                    endAdornment={props.endAdornment}
                />
            }

            {props.error &&
                <ContentExtraSmall style={{color: 'red'}}>
                    {props.errorText}
                </ContentExtraSmall>
            }
        </Box>
    )
}

const ProfileSettingCategoryPreference = () => {
    const {accessibleShops} = useContext(AppContext);
    const ShopID = accessibleShops?.length > 0 ? accessibleShops[0].ShopID : null
    const [categories, setCategories] = useState([])
    const [preferredCategoryIds, setPreferredCategoryIds] = useState([])
    const [loadingCategoryIds, setLoadingCategoryIds] = useState([])

    const fetchCategories = async () => {
        if(ShopID){
            const fetchedCategories = await callAPI(`/product/${ShopID}/categories`, "GET")
            if(fetchedCategories && fetchedCategories.length){
                setCategories(fetchedCategories.filter(c => c.categoryType === 'DEFAULT'))
            }
        }
    }

    const fetchPreferredCategoryIds = async () => {
        if(ShopID){
            const fetchedPreferredCategoryIds = await callAPI(`/user/getPreferredCategoryIds`, "GET")
            setPreferredCategoryIds(fetchedPreferredCategoryIds)
        }
    }

    const preferCategory = async (categoryId) => {
        setLoadingCategoryIds([...loadingCategoryIds, categoryId])
        const res = await callAPI(`/user/addCategoryPreference`, "POST", {query: {CategoryID: categoryId}})
        if(res){
            setPreferredCategoryIds([...preferredCategoryIds, categoryId])
        }
        setLoadingCategoryIds(loadingCategoryIds.filter(catId => catId !== categoryId))
    }

    const unpreferCategory = async (categoryId) => {
        setLoadingCategoryIds([...loadingCategoryIds, categoryId])
        const res = await callAPI(`/user/removeCategoryPreference`, "POST", {query: {CategoryID: categoryId}})
        if(res){
            setPreferredCategoryIds(preferredCategoryIds.filter(catId => catId !== categoryId))
        }
        setLoadingCategoryIds(loadingCategoryIds.filter(catId => catId !== categoryId))
    }

    const onCategoryClick = (categoryId) => {
        if(preferredCategoryIds.includes(categoryId)){
            unpreferCategory(categoryId)
        } else {
            preferCategory(categoryId)
        }
    }

    useEffect(() => {
        fetchCategories()
        fetchPreferredCategoryIds()
    }, [ShopID])

    return (
        <Box display={'flex'} flexDirection={'column'} gap={2}>
            <Headline5 translationKey="_PROFILE_SETTINGS_PREFERRED_CATEGORIES_HEADLINE_" fallbackText="Foretrukne kategorier" />
            <ContentNormal translationKey="_PROFILE_SETTINGS_PREFERRED_CATEGORIES_TEXT_" fallbackText="Vælg dine yndlingskategorier for at modtage indhold baseret på dine præferencer." />
            <Grid2 className="product-grid" container rowSpacing={1} columnSpacing={1} maxWidth={800}>
                {categories?.map(c => (
                    <Grid2
                        key={c.CategoryID}
                        size={{
                            xs: 12,
                            sm: 'auto',
                        }}
                    >
                        <Box key={c.CategoryID} height={40} display={'flex'} alignItems={'center'} paddingRight={2}>
                            <CustomCheckbox
                                checked={preferredCategoryIds.includes(c.CategoryID)}
                                loading={loadingCategoryIds.includes(c.CategoryID)}
                                label={c.categoryName}
                                onChange={() => onCategoryClick(c.CategoryID)}
                            />
                        </Box>
                    </Grid2>
                ))}
            </Grid2>
        </Box>
    )
}

export default SettingsPage